/**
 * Forehand (Player A / court-left) detection.
 *
 * Phase-cycle FSM on shoulder Δx + signed elbow–hip angle.
 * Isolated 1–2 frame elbow glitches on shoulder-open are interpolated first.
 * One discover pass: all inliers count as Forehand. Topspin / bad placement are tags.
 * Session warmup bands (when present) limit amplitude, timing, and ISI.
 * Extra topspin cue: prep racket hand left of the knee and below the hips,
 * with a longer wind-up than a table-close FH.
 *
 * Clustering window = avg peak-to-peak gap in the longest cluster × 1.25.
 * Essai = any stroke cluster (1+ strokes). Rally = cluster with ≥3 strokes.
 */

import {
  despikeIsolatedAngle,
  fillShortGaps,
  racketHandElbowDx,
  racketShoulderOpenDegreesForDetection,
  racketShoulderWristDist,
  racketWristElbowFlags,
  racketElbowInteriorDegrees,
  racketPrepTopspinCues,
  shoulderSpanNorm,
  shoulderWidthNorm,
} from "./poseAngleSeries";
import { detectServes } from "./serveHeuristics";
import {
  applyPlayerSignature,
  fitPlayerStrokeSignature,
  taggedSubset,
  topspinWarmupCounted,
} from "./playerStrokeSignature";
import { resolveEffectiveStrokeClipKind } from "./strokeClipKind";

/** Absolute floor — kills flat-angle false positives (strong Δx, no FH open). */
const REFRACTORY_MS = 320;
const MIN_AMPLITUDE_MS = 40;
const MAX_AMPLITUDE_MS = 1200;
/** Rally / essai window = avg peak-to-peak gap in longest cluster × this factor. */
const RALLY_WINDOW_GAP_FACTOR = 1.25;
const NEW_DETECT_WINDOW_MS = 1000;
/** Same-table distance drift can shrink L–R width ~20%; other tables are ~half. */
const SHOULDER_WIDTH_TOL = 0.25;
/** Ignore collapsed L–R width (≈0) for identity lock — same floor as tracker. */
const MIN_SHOULDER_WIDTH_FOR_ID = 0.02;
/** BH extra topspin cue: close-to-table ≈ near-max width; mid-far is smaller. */
const BH_CLOSE_TABLE_WIDTH_FRAC = 0.88;
const BH_MID_FAR_TABLE_WIDTH_MIN_FRAC = 0.58;
const DX_GAP_FILL_MAX_MS = 450;
const ANGLE_GAP_FILL_MAX_MS = 1000;
const SMOOTH_RADIUS = 1;
const MIN_RALLY_FOR_MODEL = 3;

// Phase-cycle FSM (signed angle as plotted)
const PREP_ANGLE_BELOW_DEG = 25;
/** Prep confirmation: shoulder Δx ≤ this × L–R shoulder width (scale-invariant). */
const TOPSPIN_PREP_DX_MAX = 0;
const FH_PREP_DX_WIDTH_FRAC = 0.15;
/** When shoulder width is missing/collapsed, fall back to this absolute Δx. */
const PREP_DX_ABS_FALLBACK = 0.02;
/** Topspin: peak > TOPSPIN_PEAK_MIN_DEG */
const TOPSPIN_PEAK_MIN_DEG = 90;
/** Forehand: TOPSPIN_PEAK_MIN_DEG exclusive upper — peak > FH_PEAK_MIN and < FH_PEAK_MAX */
const FH_PEAK_MIN_DEG = 20;
const FH_PEAK_MAX_DEG = 90;
const FOLLOW_MIN_PEAK_DEG = TOPSPIN_PEAK_MIN_DEG; // back-compat alias
const RECOVERY_ANGLE_LO_DEG = 25;
const RECOVERY_ANGLE_HI_DEG = 35;
/** Wide discovery pass: same Prep→Peak→Recovery shape, player-scaled gates. */
const DISCOVER_PEAK_MIN_DEG = 15;
const DISCOVER_PREP_BELOW_DEG = 45;
const DISCOVER_RECOVERY_FRAC = 0.28;
const DISCOVER_MIN_ANG_RISE_DEG = 15;
const ENVELOPE_MIN_STROKES = 3;
const ENVELOPE_MODE_GAP_DEG = 22;
const ENVELOPE_MIN_PER_MODE = 2;
const TREND_RUN = 2;
const DX_EPS = 0.0005;
const ANG_EPS = 0.35;
const CONTACT_DURATION_MS = 60;
const PREP_LOOKBACK_MS = 550;
/** Table-close FH prep is a fraction of a second; topspin wind-up is longer. */
const TOPSPIN_PREP_MIN_MS = 350;
const MAX_FORWARD_MS = 900;
const BH_MAX_FORWARD_MS = 1200;
const MAX_RECOVERY_MS = 1500;
/** After a real FH, keep looking for the next peak on angle+timing even if Δx stays high. */
const RALLY_PREP_RELAX_MS = 4000;
const RALLY_FORWARD_MS = 1200;
/** Rally prep can stay elevated; use prep-window valley + softer rise floor. */
const RALLY_MIN_ANG_RISE_DEG = 10;
const MAX_PREP_MS = 1100;
const MAX_FOLLOW_MS = 1100;
const CO_PEAK_SYNC_MS = 180;
const HE_DX_EPS = 0.0008;
const SW_EPS = 0.0004;

function smooth(vals, radius = SMOOTH_RADIUS) {
  const n = vals.length;
  const out = new Array(n).fill(null);
  for (let i = 0; i < n; i += 1) {
    const window = [];
    for (let j = Math.max(0, i - radius); j < Math.min(n, i + radius + 1); j += 1) {
      if (Number.isFinite(vals[j])) window.push(vals[j]);
    }
    out[i] = window.length ? window.reduce((a, b) => a + b, 0) / window.length : null;
  }
  return out;
}

function median(nums) {
  const a = nums.filter((n) => Number.isFinite(n)).sort((x, y) => x - y);
  if (!a.length) return null;
  const mid = Math.floor(a.length / 2);
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
}

function finiteVelocity(series, tMs) {
  const n = series.length;
  if (n < 2) return [];
  const out = new Array(n - 1).fill(null);
  for (let i = 0; i < n - 1; i += 1) {
    const a = series[i];
    const b = series[i + 1];
    if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
    const dt = Number(tMs[i + 1]) - Number(tMs[i]);
    if (!(dt > 0)) continue;
    out[i] = (Number(b) - Number(a)) / dt;
  }
  return out;
}

function nearestIndexByTime(tMs, tTarget) {
  const n = tMs.length;
  if (!n) return 0;
  let lo = 0;
  let hi = n;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (Number(tMs[mid]) < tTarget) lo = mid + 1;
    else hi = mid;
  }
  const i = lo;
  if (i <= 0) return 0;
  if (i >= n) return n - 1;
  return Math.abs(Number(tMs[i]) - tTarget) <= Math.abs(Number(tMs[i - 1]) - tTarget)
    ? i
    : i - 1;
}


function fallingRun(series, endI, run = TREND_RUN, eps = ANG_EPS) {
  if (endI < run) return false;
  for (let k = endI - run + 1; k <= endI; k += 1) {
    if (!Number.isFinite(series[k]) || !Number.isFinite(series[k - 1])) return false;
    if (Number(series[k]) > Number(series[k - 1]) + eps) return false;
  }
  return true;
}

function risingRun(series, endI, run = TREND_RUN, eps = ANG_EPS) {
  if (endI < run) return false;
  for (let k = endI - run + 1; k <= endI; k += 1) {
    if (!Number.isFinite(series[k]) || !Number.isFinite(series[k - 1])) return false;
    if (Number(series[k]) < Number(series[k - 1]) - eps) return false;
  }
  return true;
}

function bothRising(dx, ang, endI) {
  return risingRun(ang, endI, TREND_RUN, ANG_EPS) && risingRun(dx, endI, TREND_RUN, DX_EPS);
}

function lastAcceptedPeakMs(strokes) {
  if (!strokes.length) return null;
  const t = Number(strokes[strokes.length - 1].t_peak_ms);
  return Number.isFinite(t) ? t : null;
}

function inRallyAt(tMsVal, lastPeakMs) {
  if (lastPeakMs == null) return false;
  const dt = Number(tMsVal) - lastPeakMs;
  return dt >= REFRACTORY_MS && dt <= RALLY_PREP_RELAX_MS;
}

/** Valley: falling into this sample, next sample is no longer falling. */
function isAngleValleyTurn(ang, j) {
  return isSeriesValleyTurn(ang, j, ANG_EPS);
}

/** Local minimum turn on any smoothed series (shoulder Δx, angle, …). */
export function isSeriesValleyTurn(series, j, eps = ANG_EPS) {
  if (j + 1 >= series.length || !Number.isFinite(series[j]) || !Number.isFinite(series[j + 1])) {
    return false;
  }
  return Number(series[j + 1]) > Number(series[j]) - eps;
}

/**
 * Rally prep: angle valley (classic) or shoulder-Δx valley when angle stays
 * elevated between consecutive topspin strokes.
 */
function prepValleyAng(ang, prepStartI, forwardI, confirmI, inRally) {
  const fallback = Number.isFinite(ang[confirmI]) ? Number(ang[confirmI]) : null;
  if (!inRally) return fallback;
  let minA = fallback;
  const hi = Math.min(forwardI, ang.length - 1);
  for (let k = prepStartI; k <= hi; k += 1) {
    if (!Number.isFinite(ang[k])) continue;
    if (minA == null || Number(ang[k]) < minA) minA = Number(ang[k]);
  }
  return minA;
}

function rallyPrepOk(dx, ang, j, prepAngleBelowDeg) {
  if (
    isAngleValleyTurn(ang, j) &&
    Number(ang[j]) < Number(prepAngleBelowDeg) &&
    fallingRun(ang, j, TREND_RUN, ANG_EPS)
  ) {
    return true;
  }
  return (
    Number.isFinite(dx[j]) &&
    isSeriesValleyTurn(dx, j, DX_EPS) &&
    fallingRun(dx, j, TREND_RUN, DX_EPS)
  );
}

/** Prep Δx ceiling: `frac` of shoulder width, or a small absolute fallback. */
export function prepDxCeilingFromWidth(width, frac) {
  const f = Number(frac);
  if (!Number.isFinite(f) || f <= 0) return 0;
  const w = Number(width);
  if (Number.isFinite(w) && w >= MIN_SHOULDER_WIDTH_FOR_ID) return f * w;
  return PREP_DX_ABS_FALLBACK;
}

/**
 * Recovery after a peak: angle crossed below 35°, or (low peak ≤ 35°) landed in 25–35°.
 * Also treat the next rising run after the peak as recovery so a rally stroke
 * still counts when the 28% drop never fires before the following swing.
 */
export function isRecoveryCross(prevAng, ang, peakV, opts = {}) {
  const a = Number(ang);
  if (!Number.isFinite(a)) return false;
  const prev = Number(prevAng);
  const frac = Number(opts.recoveryFrac);
  const prep = Number(opts.prepAng);
  const peak = Number(peakV);
  if (Number.isFinite(frac) && frac > 0 && Number.isFinite(peak) && Number.isFinite(prep)) {
    const rise = peak - prep;
    if (!(rise >= DISCOVER_MIN_ANG_RISE_DEG)) return false;
    const dropNeed = Math.max(12, frac * rise);
    const level = peak - dropNeed;
    return Number.isFinite(prev) && prev > level && a <= level;
  }
  if (Number.isFinite(prev) && prev > RECOVERY_ANGLE_HI_DEG && a <= RECOVERY_ANGLE_HI_DEG) {
    return true;
  }
  return (
    Number.isFinite(peak) &&
    peak <= RECOVERY_ANGLE_HI_DEG &&
    a >= RECOVERY_ANGLE_LO_DEG &&
    a <= RECOVERY_ANGLE_HI_DEG
  );
}

function resolveRecoveryEnd(ang, tMs, peakI, peakV, prepAng, recoveryFrac) {
  const n = tMs.length;
  let recoveryEndI = peakI;
  let prevA = Number(ang[peakI]);
  let minI = peakI;
  let minV = Number.isFinite(ang[peakI]) ? Number(ang[peakI]) : Infinity;
  let increaseRun = 0;
  for (let k2 = peakI + 1; k2 < n; k2 += 1) {
    if (Number(tMs[k2]) - Number(tMs[peakI]) > MAX_RECOVERY_MS) break;
    if (!Number.isFinite(ang[k2])) continue;
    const a = Number(ang[k2]);
    if (a < minV - ANG_EPS) {
      minV = a;
      minI = k2;
      increaseRun = 0;
    } else if (Number.isFinite(prevA) && a > prevA + ANG_EPS) {
      increaseRun += 1;
    } else {
      increaseRun = 0;
    }
    if (
      isRecoveryCross(prevA, a, peakV, {
        recoveryFrac,
        prepAng,
      })
    ) {
      return k2;
    }
    if (increaseRun >= TREND_RUN && minI > peakI) {
      return minI;
    }
    prevA = a;
  }
  return minI > peakI ? minI : recoveryEndI;
}

function strokesFromPhaseCycles(
  dx,
  ang,
  tMs,
  {
    peakMinDeg = TOPSPIN_PEAK_MIN_DEG,
    peakMaxDeg = null,
    prepDxMax = TOPSPIN_PREP_DX_MAX,
    width = null,
    prepAngleBelowDeg = PREP_ANGLE_BELOW_DEG,
    recoveryFrac = null,
    minAngRise = 0,
  } = {}
) {
  const n = tMs.length;
  if (n < 8 || dx.length !== n || ang.length !== n) return [];

  const angVel = finiteVelocity(ang, tMs);
  const strokes = [];
  let i = 0;
  const prepDxFrac = Number(prepDxMax);
  const widthSeries = Array.isArray(width) && width.length === n ? width : null;

  while (i < n - 5) {
    const lastPeakMs = lastAcceptedPeakMs(strokes);
    let confirmI = null;
    for (let j = i; j < n; j += 1) {
      if (!Number.isFinite(ang[j])) continue;
      const inRally = inRallyAt(Number(tMs[j]), lastPeakMs);
      if (!inRally) {
        if (!Number.isFinite(dx[j])) continue;
        const ceiling = prepDxCeilingFromWidth(
          widthSeries ? widthSeries[j] : null,
          prepDxFrac
        );
        if (Number(dx[j]) > ceiling) continue;
      } else if (!rallyPrepOk(dx, ang, j, prepAngleBelowDeg)) {
        continue;
      }
      if (!inRally) {
        if (Number(ang[j]) >= Number(prepAngleBelowDeg)) continue;
        if (!fallingRun(ang, j, TREND_RUN, ANG_EPS)) continue;
      }
      confirmI = j;
      break;
    }
    if (confirmI == null) break;

    const tLo = Number(tMs[confirmI]) - PREP_LOOKBACK_MS;
    let prepStartI = confirmI;
    let k = confirmI;
    while (k > 0 && Number(tMs[k - 1]) >= tLo) {
      if (!Number.isFinite(ang[k - 1]) || !Number.isFinite(ang[k])) break;
      if (Number(ang[k - 1]) < Number(ang[k]) - ANG_EPS) break;
      prepStartI = k - 1;
      k -= 1;
    }

    const inRallyFwd = inRallyAt(Number(tMs[confirmI]), lastPeakMs);
    const maxFwdMs = inRallyFwd ? RALLY_FORWARD_MS : MAX_FORWARD_MS;
    let forwardI = null;
    for (let j = confirmI + 1; j < n; j += 1) {
      if (Number(tMs[j]) - Number(tMs[confirmI]) > maxFwdMs) break;
      if (inRallyFwd ? risingRun(ang, j, TREND_RUN, ANG_EPS) : bothRising(dx, ang, j)) {
        forwardI = j;
        break;
      }
    }
    if (forwardI == null) {
      i = confirmI + 1;
      continue;
    }

    let peakI = forwardI;
    let peakV = Number.isFinite(ang[forwardI]) ? Number(ang[forwardI]) : -Infinity;
    let decreaseRun = 0;
    let j = forwardI;
    while (j < n - 1) {
      if (Number(tMs[j]) - Number(tMs[forwardI]) > maxFwdMs) break;
      if (!Number.isFinite(ang[j + 1]) || !Number.isFinite(ang[j])) {
        j += 1;
        continue;
      }
      const nxt = Number(ang[j + 1]);
      const cur = Number(ang[j]);
      if (nxt > peakV) {
        peakV = nxt;
        peakI = j + 1;
      }
      if (nxt < cur - ANG_EPS) decreaseRun += 1;
      else decreaseRun = 0;
      j += 1;
      if (decreaseRun >= TREND_RUN && peakI > forwardI) break;
    }

    const prepAngForRise = prepValleyAng(ang, prepStartI, forwardI, confirmI, inRallyFwd);
    const riseFromPrep =
      prepAngForRise != null && Number.isFinite(peakV) ? peakV - prepAngForRise : null;
    const minRiseReq = inRallyFwd
      ? Math.min(Number(minAngRise) || 0, RALLY_MIN_ANG_RISE_DEG)
      : Number(minAngRise) || 0;
    const peakOk =
      Number.isFinite(peakV) &&
      (peakMaxDeg == null || peakV < peakMaxDeg) &&
      (peakMinDeg == null ||
        peakV > peakMinDeg ||
        (inRallyFwd &&
          riseFromPrep != null &&
          riseFromPrep >= minRiseReq));
    if (!peakOk) {
      i = Math.max(confirmI + 1, peakI);
      continue;
    }
    if (peakI <= forwardI) {
      i = confirmI + 1;
      continue;
    }

    if (minRiseReq > 0 && riseFromPrep != null && riseFromPrep < minRiseReq) {
      i = Math.max(confirmI + 1, peakI);
      continue;
    }

    let contactI = forwardI;
    let bestV = -Infinity;
    for (let vI = forwardI; vI < Math.min(peakI, n - 1); vI += 1) {
      const v = angVel[vI];
      if (!Number.isFinite(v)) continue;
      if (Number(v) > bestV) {
        bestV = Number(v);
        contactI = vI;
      }
    }
    if (!Number.isFinite(bestV) || bestV <= 0) {
      let bestAbs = -Infinity;
      for (let vI = forwardI; vI < Math.min(peakI, n - 1); vI += 1) {
        const v = angVel[vI];
        if (!Number.isFinite(v)) continue;
        const av = Math.abs(Number(v));
        if (av > bestAbs) {
          bestAbs = av;
          contactI = vI;
        }
      }
    }

    const tContact = Number(tMs[contactI]);
    const contactEndT = tContact + CONTACT_DURATION_MS;
    let contactEndI = contactI + 1;
    let foundContactEnd = false;
    for (let k2 = contactI + 1; k2 <= peakI; k2 += 1) {
      if (Number(tMs[k2]) >= contactEndT) {
        contactEndI = k2;
        foundContactEnd = true;
        break;
      }
    }
    if (!foundContactEnd) contactEndI = Math.min(peakI, contactI + 1);
    contactEndI = Math.max(contactI + 1, Math.min(contactEndI, peakI));

    const recoveryEndI = resolveRecoveryEnd(
      ang,
      tMs,
      peakI,
      peakV,
      prepAngForRise,
      recoveryFrac
    );

    const tValley = Number(tMs[forwardI]);
    const tPeak = Number(tMs[peakI]);
    const amp = Math.round(tPeak - tValley);
    if (amp < MIN_AMPLITUDE_MS || amp > MAX_AMPLITUDE_MS) {
      i = peakI + 1;
      continue;
    }

    const prepAng = prepAngForRise;
    const dxAtFwd = Number.isFinite(dx[forwardI]) ? Number(dx[forwardI]) : null;
    const angRise =
      riseFromPrep != null ? Number(riseFromPrep.toFixed(1)) : null;

    strokes.push({
      t_peak_ms: Math.round(tPeak),
      t_valley_ms: Math.round(tValley),
      t_contact_ms: Math.round(tContact),
      contact_source: "angle_velocity",
      amplitude_ms: amp,
      shoulder_dx: dxAtFwd != null ? Number(dxAtFwd.toFixed(4)) : null,
      angle_deg: Number(peakV.toFixed(1)),
      valley_angle_deg: prepAng != null ? Number(prepAng.toFixed(1)) : null,
      dx_rise: null,
      ang_rise: angRise,
      dt_ms: Math.round(Math.abs(tPeak - tContact)),
      dx_mode: "phase_cycle",
      phases: {
        preparation: {
          t_start_ms: Math.round(Number(tMs[prepStartI])),
          t_end_ms: Math.round(tValley),
        },
        forward_swing: {
          t_start_ms: Math.round(tValley),
          t_end_ms: Math.round(tContact),
        },
        contact: {
          t_start_ms: Math.round(tContact),
          t_end_ms: Math.round(Number(tMs[contactEndI])),
        },
        follow_through: {
          t_start_ms: Math.round(Number(tMs[contactEndI])),
          t_end_ms: Math.round(tPeak),
        },
        recovery: {
          t_start_ms: Math.round(tPeak),
          t_end_ms: Math.round(Number(tMs[recoveryEndI])),
        },
      },
    });
    i = peakI + 1;
  }

  return strokes;
}


function dedupeStrokes(strokes) {
  const sorted = [...(strokes || [])].sort(
    (a, b) => (Number(a.t_peak_ms) || 0) - (Number(b.t_peak_ms) || 0)
  );
  const merged = [];
  for (const s of sorted) {
    const tPeak = Number(s.t_peak_ms) || 0;
    const prev = merged[merged.length - 1];
    if (prev && Math.abs(tPeak - (Number(prev.t_peak_ms) || 0)) < REFRACTORY_MS) {
      if (s.amplitude_ms > prev.amplitude_ms) merged[merged.length - 1] = s;
      continue;
    }
    merged.push(s);
  }
  return merged;
}

/**
 * Drop Δx-driven hits whose elbow–hip open is tiny vs real FH strokes.
 * Absolute floor always applies; relative floor applies once ≥3 strong strokes exist.
 */

export function rallyWindowMs(strokes) {
  if (!Array.isArray(strokes) || strokes.length < 2) {
    return Math.max(REFRACTORY_MS, Math.round(REFRACTORY_MS * RALLY_WINDOW_GAP_FACTOR));
  }
  const boot = bootstrapGapMs(strokes);
  const bootClusters = buildStrokeClusters(strokes, boot);
  const longest = longestCluster(bootClusters);
  if (!longest || longest.length < 2) return boot;
  const { gaps } = gapStats(longest);
  const positive = gaps.filter((g) => g > 0);
  if (!positive.length) return boot;
  const avg = positive.reduce((a, b) => a + b, 0) / positive.length;
  return Math.max(REFRACTORY_MS, Math.round(avg * RALLY_WINDOW_GAP_FACTOR));
}

function allPeakGaps(strokes) {
  const sorted = [...(strokes || [])].sort(
    (a, b) => (Number(a.t_peak_ms) || 0) - (Number(b.t_peak_ms) || 0)
  );
  const gaps = [];
  for (let i = 1; i < sorted.length; i += 1) {
    gaps.push(
      (Number(sorted[i].t_peak_ms) || 0) - (Number(sorted[i - 1].t_peak_ms) || 0)
    );
  }
  return gaps;
}

function bootstrapGapMs(strokes) {
  const gaps = allPeakGaps(strokes).filter((g) => g > 0);
  if (!gaps.length) {
    return Math.max(REFRACTORY_MS, Math.round(REFRACTORY_MS * RALLY_WINDOW_GAP_FACTOR));
  }
  const med = median(gaps);
  return Math.max(REFRACTORY_MS, Math.round((med || REFRACTORY_MS) * RALLY_WINDOW_GAP_FACTOR));
}

function longestCluster(clusters) {
  if (!clusters?.length) return null;
  let best = clusters[0];
  for (const c of clusters) {
    if (c.length > best.length) best = c;
  }
  return best;
}

/** Consecutive stroke clusters with peak gap ≤ rally window. */
export function buildStrokeClusters(strokes, gapMs = null) {
  if (!Array.isArray(strokes) || !strokes.length) return [];
  const gap = gapMs != null ? gapMs : rallyWindowMs(strokes);
  const sorted = [...strokes].sort(
    (a, b) => (Number(a.t_peak_ms) || 0) - (Number(b.t_peak_ms) || 0)
  );
  const clusters = [];
  let cur = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = Number(sorted[i - 1].t_peak_ms) || 0;
    const next = Number(sorted[i].t_peak_ms) || 0;
    if (next - prev <= gap) {
      cur.push(sorted[i]);
    } else {
      clusters.push(cur);
      cur = [sorted[i]];
    }
  }
  clusters.push(cur);
  return clusters;
}

/**
 * Essai = every cluster (1+ strokes).
 * Rally = cluster with ≥ MIN_RALLY_FOR_MODEL strokes.
 */
function clusterMetrics(strokes, gapMs = null) {
  const gap = gapMs != null ? gapMs : rallyWindowMs(strokes);
  const clusters = buildStrokeClusters(strokes, gap);
  if (!clusters.length) {
    return {
      essai_count: 0,
      rally_count: 0,
      rally_number: 0,
      rally_window_ms: gap,
      strokes_in_rallies: 0,
      clusters: [],
    };
  }
  let maxLen = 0;
  let rallyCount = 0;
  let strokesInRallies = 0;
  for (const c of clusters) {
    if (c.length > maxLen) maxLen = c.length;
    if (c.length >= MIN_RALLY_FOR_MODEL) {
      rallyCount += 1;
      strokesInRallies += c.length;
    }
  }
  return {
    essai_count: clusters.length,
    rally_count: rallyCount,
    rally_number: maxLen,
    rally_window_ms: gap,
    strokes_in_rallies: strokesInRallies,
    clusters,
  };
}

function modeStats(peaks) {
  const med = median(peaks);
  if (med == null) return null;
  const mad = median(peaks.map((x) => Math.abs(x - med))) || 0;
  const half = Math.max(12, 2 * mad, 0.18 * Math.abs(med));
  return {
    n: peaks.length,
    med: Number(med.toFixed(1)),
    mad: Number(mad.toFixed(1)),
    lo: Number((med - half).toFixed(1)),
    hi: Number((med + half).toFixed(1)),
  };
}

function peakModes(peaks) {
  const xs = (peaks || []).filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (xs.length < ENVELOPE_MIN_STROKES) return [];
  let bestGap = -1;
  let bestI = -1;
  for (let i = 1; i < xs.length; i += 1) {
    const g = xs[i] - xs[i - 1];
    if (g > bestGap) {
      bestGap = g;
      bestI = i;
    }
  }
  const left = xs.slice(0, bestI);
  const right = xs.slice(bestI);
  if (
    bestGap >= ENVELOPE_MODE_GAP_DEG &&
    left.length >= ENVELOPE_MIN_PER_MODE &&
    right.length >= ENVELOPE_MIN_PER_MODE
  ) {
    return [modeStats(left), modeStats(right)].filter(Boolean);
  }
  const one = modeStats(xs);
  return one ? [one] : [];
}

/**
 * Typical peak band(s) from this player's repeating cycles.
 * Uses the longest rally (≥3) when present, else all discovered strokes.
 */
export function fitPlayerStrokeEnvelope(strokes) {
  const list = Array.isArray(strokes) ? strokes : [];
  const metrics = clusterMetrics(list);
  const clusters = metrics.clusters || [];
  const rallies = clusters.filter((c) => c.length >= ENVELOPE_MIN_STROKES);
  let pool = [];
  let source = "global";
  if (rallies.length) {
    pool = rallies.reduce((a, b) => (b.length > a.length ? b : a));
    source = "rally";
  } else if (list.length >= ENVELOPE_MIN_STROKES) {
    pool = list;
    source = "all";
  } else {
    return null;
  }
  const peaks = pool.map((s) => Number(s.angle_deg)).filter((n) => Number.isFinite(n));
  const modes = peakModes(peaks);
  if (!modes.length) return null;
  return { source, n: peaks.length, modes };
}

function peakInGlobalBand(peak, peakMinDeg, peakMaxDeg) {
  if (!Number.isFinite(peak)) return false;
  if (peakMinDeg != null && peak <= peakMinDeg) return false;
  if (peakMaxDeg != null && peak >= peakMaxDeg) return false;
  return true;
}

/**
 * Family inliers + tags. peakMin/peakMax ignored — topspin is a tag, not a split.
 */
export function selectStrokesForPlayerBand(
  strokes,
  _envelope,
  { sessionBase = null, sessionTopspin = null, clipKind = "" } = {}
) {
  const applied = applyPlayerSignature(strokes, {
    sessionBase,
    sessionTopspin,
    clipKind,
  });
  return {
    strokes: applied.strokes,
    appliedMode: applied.player_thresholds?.applied_mode || null,
    source: applied.player_thresholds?.source || "clip",
    envelope: applied.player_thresholds,
    player_signature: applied.player_signature,
  };
}

export function inliersOfEnvelope(strokes, envelope) {
  const list = Array.isArray(strokes) ? strokes : [];
  if (!envelope?.modes?.length) return list;
  return list.filter((s) => {
    const p = Number(s.angle_deg);
    if (!Number.isFinite(p)) return true;
    return envelope.modes.some((m) => p >= m.lo && p <= m.hi);
  });
}

function gapStats(cluster) {
  const gaps = [];
  for (let i = 1; i < cluster.length; i += 1) {
    gaps.push(
      (Number(cluster[i].t_peak_ms) || 0) - (Number(cluster[i - 1].t_peak_ms) || 0)
    );
  }
  const med = median(gaps);
  if (med == null || med <= 0) return { T: null, cv: null, gaps };
  if (gaps.length < 2) return { T: med, cv: 0, gaps };
  const mean = gaps.reduce((a, b) => a + b, 0) / gaps.length;
  const variance =
    gaps.reduce((a, b) => a + (b - mean) ** 2, 0) / gaps.length;
  const cv = mean > 0 ? Math.sqrt(variance) / mean : null;
  return { T: med, cv, gaps };
}

function attachShoulderWidths(strokes, widthSeries, tMs) {
  for (const s of strokes) {
    const t = Number(s.t_peak_ms) || 0;
    let bestI = 0;
    let bestD = Infinity;
    for (let i = 0; i < tMs.length; i += 1) {
      const d = Math.abs(tMs[i] - t);
      if (d < bestD) {
        bestD = d;
        bestI = i;
      }
    }
    const w = widthSeries[bestI];
    if (Number.isFinite(w)) s.shoulder_width_norm = Number(w.toFixed(4));
  }
  return strokes;
}

function attachBhTableDistanceCues(strokes) {
  const widths = (strokes || [])
    .map((s) => Number(s.shoulder_width_norm))
    .filter((w) => Number.isFinite(w) && w >= MIN_SHOULDER_WIDTH_FOR_ID);
  if (widths.length < 2) {
    for (const s of strokes || []) s.topspin_table_cue = false;
    return strokes;
  }
  const ref = Math.max(...widths);
  for (const s of strokes || []) {
    const w = Number(s.shoulder_width_norm);
    if (!Number.isFinite(w) || w < MIN_SHOULDER_WIDTH_FOR_ID || !(ref > 0)) {
      s.topspin_table_cue = false;
      continue;
    }
    const ratio = w / ref;
    s.table_distance_ratio = Number(ratio.toFixed(3));
    s.near_table = ratio >= BH_CLOSE_TABLE_WIDTH_FRAC;
    s.topspin_table_cue =
      ratio >= BH_MID_FAR_TABLE_WIDTH_MIN_FRAC && ratio < BH_CLOSE_TABLE_WIDTH_FRAC;
  }
  return strokes;
}

function attachTopspinPrepCues(strokes, samples, viewSetup) {
  const ordered = [...(samples || [])].sort(
    (a, b) => (Number(a.t_ms) || 0) - (Number(b.t_ms) || 0)
  );
  for (const stroke of strokes || []) {
    const prep = stroke?.phases?.preparation || {};
    const t0 = Number(prep.t_start_ms);
    const t1 = Number(prep.t_end_ms);
    if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 < t0) continue;
    const dur = Math.round(t1 - t0);
    stroke.prep_duration_ms = dur;
    let sawLeft = false;
    let sawBelow = false;
    let sawBoth = false;
    let anyPose = false;
    for (const s of ordered) {
      const t = Number(s.t_ms) || 0;
      if (t < t0 || t > t1) continue;
      const cues = racketPrepTopspinCues(s.left?.joints, "left", viewSetup);
      if (!cues) continue;
      anyPose = true;
      if (cues.left_of_knee === true) sawLeft = true;
      if (cues.below_hips === true) sawBelow = true;
      if (cues.left_of_knee === true && cues.below_hips === true) {
        sawBoth = true;
        break;
      }
    }
    if (anyPose) {
      stroke.prep_wrist_left_of_knee = sawLeft;
      stroke.prep_wrist_below_hips = sawBelow;
      stroke.topspin_prep_pose = sawBoth;
    }
    stroke.topspin_prep_cue = Boolean(sawBoth && dur >= TOPSPIN_PREP_MIN_MS);
  }
  return strokes;
}

function shoulderWidthRefFromCluster(cluster) {
  const ws = (cluster || [])
    .map((s) => Number(s.shoulder_width_norm))
    .filter((w) => Number.isFinite(w) && w >= MIN_SHOULDER_WIDTH_FOR_ID);
  if (!ws.length) return null;
  return ws.reduce((a, b) => a + b, 0) / ws.length;
}

/**
 * Near-table lock: among rally (≥3) clusters, prefer the largest avg L–R
 * shoulder width (farther / other-table players project smaller). Falls back
 * to any cluster when no rally exists.
 */
function shoulderWidthRefFromClusters(clusters) {
  const list = clusters || [];
  const rallies = list.filter((c) => c.length >= MIN_RALLY_FOR_MODEL);
  const pool = rallies.length ? rallies : list;
  let bestW = null;
  let bestLen = -1;
  for (const c of pool) {
    const w = shoulderWidthRefFromCluster(c);
    if (w == null) continue;
    if (
      bestW == null ||
      w > bestW + 1e-6 ||
      (Math.abs(w - bestW) <= 1e-6 && c.length > bestLen)
    ) {
      bestW = w;
      bestLen = c.length;
    }
  }
  return bestW;
}

function leftPosePresent(sample) {
  return Array.isArray(sample?.left?.joints) && sample.left.joints.length >= 13;
}

/**
 * Reject pose-presence segments whose early usable L–R shoulder width is
 * significantly *smaller* than the near-table ref (farther / other-table).
 * Larger-or-similar widths are kept. Collapsed frames are skipped so the
 * lock starts at the first usable shoulders.
 */
function rejectedDetectionRanges(ordered, widthSeries, tMs, refWidth) {
  if (!Number.isFinite(refWidth) || refWidth <= 0) return [];
  const rejected = [];
  let i = 0;
  while (i < ordered.length) {
    if (!leftPosePresent(ordered[i])) {
      i += 1;
      continue;
    }
    const startI = i;
    const startMs = tMs[i];
    while (i < ordered.length && leftPosePresent(ordered[i])) i += 1;
    const endI = i - 1;
    const endMs = tMs[endI];

    let maxW = null;
    let lockStartMs = null;
    for (let j = startI; j <= endI; j += 1) {
      const w = widthSeries[j];
      if (!Number.isFinite(w) || w < MIN_SHOULDER_WIDTH_FOR_ID) continue;
      if (lockStartMs == null) lockStartMs = tMs[j];
      if (tMs[j] > lockStartMs + NEW_DETECT_WINDOW_MS) break;
      maxW = maxW == null ? w : Math.max(maxW, w);
    }
    if (maxW == null) continue;
    // Other tables project smaller — only reject clearly-smaller resumes.
    if (maxW < refWidth * (1 - SHOULDER_WIDTH_TOL)) {
      rejected.push({ tStart: startMs, tEnd: endMs });
    }
  }
  return rejected;
}

function strokeInRejected(stroke, rejected) {
  if (!rejected?.length) return false;
  const t = Number(stroke.t_peak_ms) || 0;
  return rejected.some((r) => t >= r.tStart && t <= r.tEnd);
}

function filterStrokesOutsideRejected(strokes, rejected) {
  if (!rejected?.length) return strokes;
  return strokes.filter((s) => !strokeInRejected(s, rejected));
}

/** Drop strokes whose apparent size is clearly smaller than the near-table lock. */
function filterStrokesByShoulderWidth(strokes, refWidth, tol = SHOULDER_WIDTH_TOL) {
  if (!Number.isFinite(refWidth) || refWidth <= 0) return strokes;
  const floor = refWidth * (1 - tol);
  return strokes.filter((s) => {
    const w = Number(s.shoulder_width_norm);
    // Missing / collapsed width: keep (identity unknown).
    if (!Number.isFinite(w) || w < MIN_SHOULDER_WIDTH_FOR_ID) return true;
    return w >= floor;
  });
}

function applyShoulderIdentityFilters(strokes, ordered, widthSeries, tMs, refWidth) {
  let rejected = rejectedDetectionRanges(ordered, widthSeries, tMs, refWidth);
  let next = filterStrokesOutsideRejected(strokes, rejected);
  // Sticky single-track self-kill: rejecting the only segment would wipe the
  // same strokes that built the lock — fall back to per-stroke size only.
  if (strokes.length > 0 && next.length < Math.max(1, strokes.length * 0.5)) {
    rejected = [];
    next = strokes;
  }
  next = filterStrokesByShoulderWidth(next, refWidth);
  return { strokes: next, rejected };
}

export const RALLY_WINDOW_GAP_FACTOR_EXPORT = RALLY_WINDOW_GAP_FACTOR;
/** @deprecated Use rallyWindowMs(strokes). */
export const RALLY_GAP_MS_EXPORT = Math.round(REFRACTORY_MS * RALLY_WINDOW_GAP_FACTOR);
export const MIN_RALLY_STROKES = MIN_RALLY_FOR_MODEL;

function emptyResult() {
  return emptyResultFor("fh_topspin_left", "Forehand topspin");
}

function amplitudeStats(strokes) {
  const amps = strokes.map((s) => s.amplitude_ms).filter((a) => Number.isFinite(a));
  if (!amps.length) return { min: null, avg: null, max: null };
  return {
    min: Math.min(...amps),
    avg: Math.round(amps.reduce((a, b) => a + b, 0) / amps.length),
    max: Math.max(...amps),
  };
}

function coachExtras(strokes, metrics) {
  const essai = metrics.essai_count || 0;
  const strokeCount = strokes.length;
  const longest = longestCluster(metrics.clusters);
  const { T } = gapStats(longest || []);
  return {
    strokes_per_essai:
      essai > 0 ? Math.round((strokeCount / essai) * 100) / 100 : null,
    pct_strokes_in_rallies:
      strokeCount > 0
        ? Math.round(((metrics.strokes_in_rallies || 0) / strokeCount) * 100)
        : null,
    inter_stroke_interval_ms: T != null ? Math.round(T) : null,
  };
}


/**
 * @param {Array} samples pose result samples
 * @param {object|null} viewSetup display orientation (for shoulder Δx + racket arm)
 */


function emptyResultFor(id, label) {
  return {
    id,
    player: "left",
    label,
    stroke_count: 0,
    essai_count: 0,
    rally_count: 0,
    rally_number: 0,
    rally_window_ms: Math.round(REFRACTORY_MS * RALLY_WINDOW_GAP_FACTOR),
    strokes_per_essai: null,
    pct_strokes_in_rallies: null,
    inter_stroke_interval_ms: null,
    shoulder_width_ref: null,
    rejected_segments: 0,
    strokes_in_rallies: 0,
    amplitude_ms: { min: null, avg: null, max: null },
    strokes: [],
    rally_model: null,
    player_thresholds: null,
    player_signature: null,
  };
}

function detectionFromStrokes(id, label, strokes, extras = {}) {
  const metrics = clusterMetrics(strokes);
  const coach = coachExtras(strokes, metrics);
  const missingTrunk = strokes.filter((s) =>
    Array.isArray(s?.tags) && s.tags.includes("missing_trunk_rotation")
  ).length;
  return {
    id,
    player: "left",
    label,
    stroke_count: strokes.length,
    essai_count: metrics.essai_count,
    rally_count: metrics.rally_count,
    rally_number: metrics.rally_number,
    rally_window_ms: metrics.rally_window_ms,
    strokes_in_rallies: metrics.strokes_in_rallies || 0,
    strokes_per_essai: coach.strokes_per_essai,
    pct_strokes_in_rallies: coach.pct_strokes_in_rallies,
    inter_stroke_interval_ms: coach.inter_stroke_interval_ms,
    missing_trunk_rotation_count: missingTrunk,
    shoulder_width_ref: extras.shoulder_width_ref ?? null,
    rejected_segments: extras.rejected_segments ?? 0,
    amplitude_ms: amplitudeStats(strokes),
    strokes,
    rally_model: null,
    player_thresholds: extras.player_thresholds || null,
    player_signature: extras.player_signature || null,
    ...(extras.fields || {}),
  };
}

function discoverFhStrokes(samples, viewSetup, seriesFns = FE_SERIES) {
  if (!Array.isArray(samples) || !samples.length) return [];
  const ordered = [...samples].sort(
    (a, b) => (Number(a.t_ms) || 0) - (Number(b.t_ms) || 0)
  );
  const tMs = ordered.map((s) => Number(s.t_ms) || 0);
  const { angRawFn, dxFn, widthFn } = seriesFns;
  const angRaw = ordered.map((s) => angRawFn(s, viewSetup));
  const widthSeries = ordered.map((s) => widthFn(s, viewSetup));
  const dxFilled = fillShortGaps(
    ordered.map((s) => dxFn(s, viewSetup)),
    tMs,
    DX_GAP_FILL_MAX_MS
  );
  const angFilled = despikeIsolatedAngle(
    fillShortGaps(angRaw, tMs, ANGLE_GAP_FILL_MAX_MS, {
      preservePeaks: true,
    }),
    tMs
  );
  const dxSmooth = smooth(dxFilled);
  const ang = smooth(angFilled);
  const discovered = dedupeStrokes(
    strokesFromPhaseCycles(dxSmooth, ang, tMs, {
      peakMinDeg: DISCOVER_PEAK_MIN_DEG,
      peakMaxDeg: null,
      prepDxMax: FH_PREP_DX_WIDTH_FRAC,
      width: widthSeries,
      prepAngleBelowDeg: DISCOVER_PREP_BELOW_DEG,
      recoveryFrac: DISCOVER_RECOVERY_FRAC,
      minAngRise: DISCOVER_MIN_ANG_RISE_DEG,
    })
  );
  attachShoulderWidths(discovered, widthSeries, tMs);
  attachTopspinPrepCues(discovered, ordered, viewSetup);
  return discovered;
}

export function detectFhFamily(samples, viewSetup = null, opts = {}) {
  const discovered = discoverFhStrokes(samples, viewSetup);
  const clipKind = resolveEffectiveStrokeClipKind({
    recipeKind: opts.clipKind || "",
    recipeKinds: opts.recipeKinds || null,
    ...(opts.exerciseLabels && typeof opts.exerciseLabels === "object"
      ? opts.exerciseLabels
      : {}),
  });
  const applied = applyPlayerSignature(discovered, {
    sessionBase: opts.playerThresholds?.forehand || null,
    sessionTopspin: opts.playerThresholds?.forehand_topspin || null,
    clipKind,
    clusters: clusterMetrics(discovered).clusters,
  });
  const extra = {
    player_thresholds: applied.player_thresholds,
    player_signature: applied.player_signature,
  };
  return {
    fh_left: detectionFromStrokes("fh_left", "Forehand", applied.strokes, extra),
    fh_topspin_left: detectionFromStrokes(
      "fh_topspin_left",
      "Forehand topspin",
      topspinWarmupCounted(applied.strokes, {
        clipKind,
        familyTag: "forehand",
      }),
      extra
    ),
  };
}

const FE_SERIES = {
  angRawFn: (s, viewSetup) =>
    racketShoulderOpenDegreesForDetection(s.left?.joints, "left", viewSetup),
  dxFn: (s, viewSetup) => shoulderSpanNorm(s.left?.joints, "left", viewSetup),
  widthFn: (s, viewSetup) => shoulderWidthNorm(s.left?.joints, viewSetup),
};

/**
 * @param {Array} samples pose result samples
 * @param {object|null} viewSetup display orientation (for shoulder Δx + racket arm)
 */
export function detectFhTopspinLeft(samples, viewSetup = null, opts = {}) {
  return detectFhFamily(samples, viewSetup, opts).fh_topspin_left;
}

export function detectFhLeft(samples, viewSetup = null, opts = {}) {
  return detectFhFamily(samples, viewSetup, opts).fh_left;
}

function emptyBhResult() {
  return {
    id: "bh_topspin_left",
    player: "left",
    label: "Backhand topspin",
    stroke_count: 0,
    wrist_power_count: 0,
    essai_count: 0,
    rally_count: 0,
    rally_number: 0,
    rally_window_ms: Math.round(REFRACTORY_MS * RALLY_WINDOW_GAP_FACTOR),
    strokes_per_essai: null,
    pct_strokes_in_rallies: null,
    inter_stroke_interval_ms: null,
    shoulder_width_ref: null,
    rejected_segments: 0,
    strokes_in_rallies: 0,
    amplitude_ms: { min: null, avg: null, max: null },
    strokes: [],
    rally_model: null,
    player_thresholds: null,
  };
}

function strokesFromBhPhaseCycles(ang, heDx, below, left, above, right, tMs) {
  const n = tMs.length;
  if (
    n < 10 ||
    [ang, heDx, below, left, above, right].some((s) => s.length !== n)
  ) {
    return [];
  }
  const strokes = [];
  let i = 0;
  while (i < n - 6) {
    let confirmI = null;
    for (let j = i; j < n; j += 1) {
      if (!Number.isFinite(ang[j])) continue;
      if (!fallingRun(ang, j, TREND_RUN, ANG_EPS)) continue;
      confirmI = j;
      break;
    }
    if (confirmI == null) break;

    const tLo = Number(tMs[confirmI]) - PREP_LOOKBACK_MS;
    let prepStartI = confirmI;
    let k = confirmI;
    while (k > 0 && Number(tMs[k - 1]) >= tLo) {
      if (!Number.isFinite(ang[k - 1]) || !Number.isFinite(ang[k])) break;
      if (Number(ang[k - 1]) < Number(ang[k]) - ANG_EPS) break;
      prepStartI = k - 1;
      k -= 1;
    }

    let heMin = Infinity;
    let heMinI = null;
    let sawLeft = false;
    let turnI = null;
    for (let j = prepStartI; j < n - 1; j += 1) {
      if (Number(tMs[j]) - Number(tMs[confirmI]) > MAX_PREP_MS) break;
      if (left[j] === true) sawLeft = true;
      if (Number.isFinite(heDx[j])) {
        const v = Number(heDx[j]);
        if (v < heMin - HE_DX_EPS) {
          heMin = v;
          heMinI = j;
        }
      }
      if (
        sawLeft &&
        heMinI != null &&
        heMin < 0 &&
        j >= heMinI + TREND_RUN &&
        (risingRun(heDx, j, TREND_RUN, HE_DX_EPS) ||
          risingRun(ang, j, TREND_RUN, ANG_EPS))
      ) {
        turnI = heMinI;
        break;
      }
    }
    if (turnI == null || !sawLeft || !(heMin < 0)) {
      i = confirmI + 1;
      continue;
    }

    // Advance past both confirm and turn on failure (turnI can be < confirmI).
    const afterTurn = Math.max(confirmI, turnI) + 1;
    let contactI = null;
    for (let j = turnI + 1; j < n; j += 1) {
      if (Number(tMs[j]) - Number(tMs[turnI]) > BH_MAX_FORWARD_MS) break;
      // Wrist above elbow is enough. Wrist-right dropped real BH topspins
      // on the left-side player (wrist stays left on camera).
      if (above[j] === true) {
        if (risingRun(ang, j, TREND_RUN, ANG_EPS) || j > turnI + TREND_RUN) {
          contactI = j;
          break;
        }
      }
    }
    if (contactI == null) {
      i = afterTurn;
      continue;
    }

    const tContact = Number(tMs[contactI]);
    const contactEndT = tContact + CONTACT_DURATION_MS;
    let contactEndI = contactI + 1;
    for (let k2 = contactI + 1; k2 < n; k2 += 1) {
      if (Number(tMs[k2]) >= contactEndT) {
        contactEndI = k2;
        break;
      }
    }
    contactEndI = Math.max(contactI + 1, Math.min(contactEndI, n - 1));

    let angPeakI = contactI;
    let angPeakV = Number.isFinite(ang[contactI]) ? Number(ang[contactI]) : -Infinity;
    let angDec = 0;
    for (let j = contactI; j < n - 1; j += 1) {
      if (Number(tMs[j]) - tContact > MAX_FOLLOW_MS) break;
      if (Number.isFinite(ang[j + 1]) && Number.isFinite(ang[j])) {
        const nxtA = Number(ang[j + 1]);
        const curA = Number(ang[j]);
        if (nxtA > angPeakV) {
          angPeakV = nxtA;
          angPeakI = j + 1;
          angDec = 0;
        } else if (nxtA < curA - ANG_EPS) angDec += 1;
        else angDec = 0;
      }
      if (angDec >= TREND_RUN && angPeakI > contactI) break;
    }
    if (!Number.isFinite(angPeakV) || angPeakI <= contactI) {
      i = contactI + 1;
      continue;
    }

    const tPeak = Number(tMs[angPeakI]);
    let recoveryEndI = null;
    for (let k2 = angPeakI + 1; k2 < n; k2 += 1) {
      if (Number(tMs[k2]) - tPeak > MAX_RECOVERY_MS) break;
      if (below[k2] === true) {
        recoveryEndI = k2;
        break;
      }
    }
    if (recoveryEndI == null) {
      i = angPeakI + 1;
      continue;
    }

    const tValley = Number(tMs[turnI]);
    const amp = Math.round(tPeak - tValley);
    if (amp < MIN_AMPLITUDE_MS || amp > MAX_AMPLITUDE_MS) {
      i = recoveryEndI + 1;
      continue;
    }

    const prepAng = Number.isFinite(ang[confirmI]) ? Number(ang[confirmI]) : null;
    strokes.push({
      t_peak_ms: Math.round(tPeak),
      t_valley_ms: Math.round(tValley),
      t_contact_ms: Math.round(tContact),
      contact_source: "wrist_geometry",
      amplitude_ms: amp,
      angle_deg: Number(angPeakV.toFixed(1)),
      he_dx_min: Number.isFinite(heDx[turnI])
        ? Number(Number(heDx[turnI]).toFixed(4))
        : null,
      valley_angle_deg: prepAng != null ? Number(prepAng.toFixed(1)) : null,
      ang_rise: prepAng != null ? Number((angPeakV - prepAng).toFixed(1)) : null,
      dx_mode: "bh_phase_cycle",
      wrist_power: true,
      phases: {
        preparation: {
          t_start_ms: Math.round(Number(tMs[prepStartI])),
          t_end_ms: Math.round(Number(tMs[turnI])),
        },
        forward_swing: {
          t_start_ms: Math.round(Number(tMs[turnI])),
          t_end_ms: Math.round(tContact),
        },
        contact: {
          t_start_ms: Math.round(tContact),
          t_end_ms: Math.round(Number(tMs[contactEndI])),
        },
        follow_through: {
          t_start_ms: Math.round(Number(tMs[contactEndI])),
          t_end_ms: Math.round(tPeak),
        },
        recovery: {
          t_start_ms: Math.round(tPeak),
          t_end_ms: Math.round(Number(tMs[recoveryEndI])),
        },
      },
    });
    i = recoveryEndI + 1;
  }
  return strokes;
}

function discoverBhLeftStrokes(samples, viewSetup = null) {
  if (!Array.isArray(samples) || !samples.length) return [];
  const ordered = [...samples].sort(
    (a, b) => (Number(a.t_ms) || 0) - (Number(b.t_ms) || 0)
  );
  const tMs = ordered.map((s) => Number(s.t_ms) || 0);
  const angRaw = ordered.map((s) =>
    racketShoulderOpenDegreesForDetection(s.left?.joints, "left", viewSetup)
  );
  const elbRaw = ordered.map((s) =>
    racketElbowInteriorDegrees(s.left?.joints, "left", viewSetup)
  );
  const distRaw = ordered.map((s) =>
    racketShoulderWristDist(s.left?.joints, "left", viewSetup)
  );
  const below = ordered.map((s) => {
    const flags = racketWristElbowFlags(s.left?.joints, "left", viewSetup);
    return flags ? flags.below : null;
  });
  const widthSeries = ordered.map((s) =>
    shoulderWidthNorm(s.left?.joints, viewSetup)
  );
  const ang = smooth(
    despikeIsolatedAngle(
      fillShortGaps(angRaw, tMs, ANGLE_GAP_FILL_MAX_MS, { preservePeaks: true }),
      tMs
    )
  );
  const elbow = smooth(
    fillShortGaps(elbRaw, tMs, ANGLE_GAP_FILL_MAX_MS, { preservePeaks: true })
  );
  const dist = smooth(fillShortGaps(distRaw, tMs, DX_GAP_FILL_MAX_MS));
  const localMax = (vals, eps) => {
    const peaks = [];
    for (let i = 1; i < vals.length - 1; i += 1) {
      if (!Number.isFinite(vals[i]) || !Number.isFinite(vals[i - 1]) || !Number.isFinite(vals[i + 1])) continue;
      if (vals[i] >= vals[i - 1] - eps && vals[i] > vals[i + 1] + eps) peaks.push(i);
    }
    return peaks;
  };
  const nearest = (peaks, t0, windowMs) => {
    let best = null;
    let bestDt = windowMs + 1;
    for (const i of peaks) {
      const dt = Math.abs(tMs[i] - t0);
      if (dt <= windowMs && dt < bestDt) {
        best = i;
        bestDt = dt;
      }
    }
    return best;
  };
  const angPeaks = localMax(ang, ANG_EPS);
  const elbPeaks = localMax(elbow, 0.4);
  const distPeaks = localMax(dist, 0.0004);
  const raw = [];
  let lastT = -1e9;
  for (const ai of angPeaks) {
    const t0 = tMs[ai];
    if (t0 - lastT < REFRACTORY_MS) continue;
    const ei = nearest(elbPeaks, t0, 80);
    const di = nearest(distPeaks, t0, 80);
    if (ei == null || di == null) continue;
    const tLo = t0 - PREP_LOOKBACK_MS;
    const prep = below.some((b, i) => b === true && tMs[i] >= tLo && tMs[i] <= t0);
    if (!prep) continue;
    const tContact = Math.round((t0 + tMs[ei] + tMs[di]) / 3);
    let tValley = null;
    for (let k = ai; k >= 0; k -= 1) {
      if (below[k] === true) {
        tValley = Math.round(tMs[k]);
        break;
      }
    }
    let amp = null;
    if (tValley != null) {
      amp = tContact - tValley;
      if (amp < MIN_AMPLITUDE_MS || amp > MAX_AMPLITUDE_MS) continue;
    }
    raw.push({
      t_peak_ms: Math.round(t0),
      t_valley_ms: tValley,
      t_contact_ms: tContact,
      contact_source: "co_peak",
      amplitude_ms: amp,
      angle_deg: Number.isFinite(ang[ai]) ? Math.round(ang[ai] * 10) / 10 : null,
      elbow_angle_deg: Number.isFinite(elbow[ei]) ? Math.round(elbow[ei] * 10) / 10 : null,
      shoulder_wrist_dist: Number.isFinite(dist[di]) ? Math.round(dist[di] * 10000) / 10000 : null,
      dx_mode: "bh_co_peak",
      phases: synthesizeCoPeakPhases({
        t_valley_ms: tValley,
        t_contact_ms: tContact,
        t_peak_ms: Math.round(t0),
      }),
    });
    lastT = t0;
  }
  const found = dedupeStrokes(raw);
  attachShoulderWidths(found, widthSeries, tMs);
  return found;
}

function finishBhDetection(id, label, found, opts = {}, extraFields = {}) {
  const applied = applyPlayerSignature(found, {
    sessionBase: opts.playerThresholds?.backhand || null,
    sessionTopspin: opts.playerThresholds?.backhand_topspin || null,
    clipKind: opts.clipKind || "",
    clusters: clusterMetrics(found).clusters,
  });
  return detectionFromStrokes(id, label, applied.strokes, {
    player_thresholds: applied.player_thresholds,
    player_signature: applied.player_signature,
    fields: extraFields,
  });
}

export function detectBhFamily(samples, viewSetup = null, opts = {}) {
  const driveFound = discoverBhLeftStrokes(samples, viewSetup);
  const tsFound = discoverBhTopspinStrokes(samples, viewSetup);
  const merged = dedupeStrokes([...(driveFound || []), ...(tsFound || [])]);
  attachBhTableDistanceCues(merged);
  const clipKind = resolveEffectiveStrokeClipKind({
    recipeKind: opts.clipKind || "",
    recipeKinds: opts.recipeKinds || null,
    ...(opts.exerciseLabels && typeof opts.exerciseLabels === "object"
      ? opts.exerciseLabels
      : {}),
  });
  const applied = applyPlayerSignature(merged, {
    sessionBase: opts.playerThresholds?.backhand || null,
    sessionTopspin: opts.playerThresholds?.backhand_topspin || null,
    clipKind,
    clusters: clusterMetrics(merged).clusters,
  });
  const extra = {
    player_thresholds: applied.player_thresholds,
    player_signature: applied.player_signature,
  };
  const counted = topspinWarmupCounted(applied.strokes, {
    clipKind,
    familyTag: "backhand",
  });
  const wristPowerCount = counted.filter((s) => s.wrist_power).length;
  return {
    bh_left: detectionFromStrokes("bh_left", "Backhand", applied.strokes, extra),
    bh_topspin_left: detectionFromStrokes(
      "bh_topspin_left",
      "Backhand topspin",
      counted,
      { ...extra, fields: { wrist_power_count: wristPowerCount } }
    ),
  };
}

export function detectBhLeft(samples, viewSetup = null, opts = {}) {
  return detectBhFamily(samples, viewSetup, opts).bh_left;
}

function discoverBhTopspinStrokes(samples, viewSetup = null) {
  if (!Array.isArray(samples) || !samples.length) return [];
  const ordered = [...samples].sort(
    (a, b) => (Number(a.t_ms) || 0) - (Number(b.t_ms) || 0)
  );
  const tMs = ordered.map((s) => Number(s.t_ms) || 0);
  const angRaw = ordered.map((s) =>
    racketShoulderOpenDegreesForDetection(s.left?.joints, "left", viewSetup)
  );
  const heRaw = ordered.map((s) =>
    racketHandElbowDx(s.left?.joints, "left", viewSetup)
  );
  const below = [];
  const left = [];
  const above = [];
  const right = [];
  const widthSeries = ordered.map((s) =>
    shoulderWidthNorm(s.left?.joints, viewSetup)
  );
  for (const s of ordered) {
    const flags = racketWristElbowFlags(s.left?.joints, "left", viewSetup);
    below.push(flags ? flags.below : null);
    left.push(flags ? flags.left : null);
    above.push(flags ? flags.above : null);
    right.push(flags ? flags.right : null);
  }
  const ang = smooth(
    despikeIsolatedAngle(
      fillShortGaps(angRaw, tMs, ANGLE_GAP_FILL_MAX_MS, { preservePeaks: true }),
      tMs
    )
  );
  const heDx = smooth(fillShortGaps(heRaw, tMs, DX_GAP_FILL_MAX_MS));
  const found = dedupeStrokes(
    strokesFromBhPhaseCycles(ang, heDx, below, left, above, right, tMs)
  );
  attachShoulderWidths(found, widthSeries, tMs);
  return found;
}

export function detectBhTopspinLeft(samples, viewSetup = null, opts = {}) {
  return detectBhFamily(samples, viewSetup, opts).bh_topspin_left;
}

function summaryBlock(det) {
  const amp = det?.amplitude_ms || {};
  return {
    count: Number(det?.stroke_count) || 0,
    essai_count: Number(det?.essai_count) || 0,
    rally_count: Number(det?.rally_count) || 0,
    rally_number: Number(det?.rally_number) || 0,
    rally_window_ms: Number(det?.rally_window_ms) || null,
    strokes_per_essai: det?.strokes_per_essai ?? null,
    pct_strokes_in_rallies: det?.pct_strokes_in_rallies ?? null,
    inter_stroke_interval_ms: det?.inter_stroke_interval_ms ?? null,
    strokes_in_rallies: Number(det?.strokes_in_rallies) || 0,
    shoulder_width_ref: det?.shoulder_width_ref ?? null,
    rejected_segments: Number(det?.rejected_segments) || 0,
    amplitude_ms_min: amp.min ?? null,
    amplitude_ms_avg: amp.avg ?? null,
    amplitude_ms_max: amp.max ?? null,
    player: "left",
  };
}

/** Canonical FH phase order for live overlays / charts. */
export const FH_TOPSPIN_PHASE_ORDER = [
  "preparation",
  "forward_swing",
  "contact",
  "follow_through",
  "recovery",
];

/** Prep → contact → follow envelopes from co-peak timestamps (BH drive). */
export function synthesizeCoPeakPhases(stroke, contactMs = CONTACT_DURATION_MS) {
  const tValley = Number(stroke?.t_valley_ms);
  const tContact = Number(stroke?.t_contact_ms);
  const tPeak = Number(stroke?.t_peak_ms);
  const phases = {};
  if (Number.isFinite(tValley) && Number.isFinite(tContact) && tValley <= tContact) {
    phases.preparation = { t_start_ms: tValley, t_end_ms: tContact };
  }
  if (Number.isFinite(tContact)) {
    const contactEnd = tContact + Math.max(16, Number(contactMs) || CONTACT_DURATION_MS);
    phases.contact = { t_start_ms: tContact, t_end_ms: contactEnd };
    if (Number.isFinite(tPeak) && tPeak > contactEnd) {
      phases.follow_through = { t_start_ms: contactEnd, t_end_ms: tPeak };
    }
  }
  return Object.keys(phases).length ? phases : null;
}

export function phasesOfStroke(stroke) {
  const existing = stroke?.phases;
  if (existing && typeof existing === "object") {
    for (const key of FH_TOPSPIN_PHASE_ORDER) {
      const r = existing[key];
      const t0 = Number(r?.t_start_ms);
      const t1 = Number(r?.t_end_ms);
      if (Number.isFinite(t0) && Number.isFinite(t1)) return existing;
    }
  }
  return synthesizeCoPeakPhases(stroke);
}

/**
 * Resolve which stroke phase is active at `tMs` (video timeline ms).
 * Works for FH and BH detections (stored envelopes, or co-peak timestamps).
 * Returns `{ phaseKey, stroke }` or null when outside all phase envelopes.
 */
export function resolveFhTopspinPhaseAtMs(detection, tMs) {
  const strokes = Array.isArray(detection?.strokes) ? detection.strokes : [];
  if (!strokes.length || !Number.isFinite(Number(tMs))) return null;
  const t = Number(tMs);

  let best = null;
  let bestPeakDist = Infinity;
  for (const s of strokes) {
    const phases = phasesOfStroke(s);
    if (!phases || typeof phases !== "object") continue;
    const tPeak = Number(s?.t_peak_ms);
    const peakDist = Number.isFinite(tPeak) ? Math.abs(tPeak - t) : Infinity;

    for (const key of FH_TOPSPIN_PHASE_ORDER) {
      const r = phases[key];
      if (!r) continue;
      const t0 = Number(r.t_start_ms);
      const t1 = Number(r.t_end_ms);
      if (!Number.isFinite(t0) || !Number.isFinite(t1)) continue;
      const lo = Math.min(t0, t1);
      const hi = Math.max(t0, t1);
      if (t < lo || t > hi) continue;
      if (peakDist <= bestPeakDist) {
        bestPeakDist = peakDist;
        best = { phaseKey: key, stroke: s };
      }
    }
  }
  return best;
}

/**
 * Prefer the active phase whose stroke peak is nearest to `tMs`
 * across topspin + forehand detections.
 */
export function resolveFhPhaseAtMs(detections, tMs) {
  const list = Array.isArray(detections)
    ? detections
    : [
        detections?.fh_topspin_left,
        detections?.fh_left,
        detections?.bh_topspin_left,
        detections?.bh_left,
      ].filter(Boolean);
  let best = null;
  let bestPeakDist = Infinity;
  for (const det of list) {
    const hit = resolveFhTopspinPhaseAtMs(det, tMs);
    if (!hit) continue;
    const tPeak = Number(hit.stroke?.t_peak_ms);
    const peakDist = Number.isFinite(tPeak)
      ? Math.abs(tPeak - Number(tMs))
      : Infinity;
    if (peakDist <= bestPeakDist) {
      bestPeakDist = peakDist;
      best = {
        ...hit,
        detectionId: det?.id || null,
        label: det?.label || null,
      };
    }
  }
  return best;
}

export function buildPoseSummaryFromDetections(detections) {
  const topspin = detections?.fh_topspin_left || {};
  const fh = detections?.fh_left || {};
  const bhTop = detections?.bh_topspin_left || {};
  const bh = detections?.bh_left || {};
  const bhTopBlock = summaryBlock(bhTop);
  bhTopBlock.wrist_power_count = Number(bhTop.wrist_power_count) || 0;
  return {
    forehand_count: Number(fh.stroke_count) || 0,
    backhand_count: Number(bh.stroke_count) || 0,
    serve_count: Number(detections?.serve_toss?.serve_count) || 0,
    serve_count_a: Number(detections?.serve_toss?.serve_count_a) || 0,
    serve_count_b: Number(detections?.serve_toss?.serve_count_b) || 0,
    forehand: summaryBlock(fh),
    forehand_topspin: summaryBlock(topspin),
    backhand: summaryBlock(bh),
    backhand_topspin: bhTopBlock,
    detections: detections || {},
  };
}

export function runPoseHeuristics(
  samples,
  heuristicIds,
  viewSetup = null,
  tableCorners = null,
  opts = {}
) {
  const detections = {};
  const ids = heuristicIds || [];
  const playerThresholds = opts.playerThresholds || null;
  const clipKind = opts.clipKind || "";
  const exerciseLabels = opts.exerciseLabels || null;
  const wantFh = ids.includes("fh_left") || ids.includes("fh_topspin_left");
  const wantBh = ids.includes("bh_left") || ids.includes("bh_topspin_left");
  if (wantFh) {
    const fam = detectFhFamily(samples, viewSetup, {
      playerThresholds,
      clipKind,
      exerciseLabels,
    });
    detections.fh_left = fam.fh_left;
    detections.fh_topspin_left = fam.fh_topspin_left;
  }
  if (wantBh) {
    const fam = detectBhFamily(samples, viewSetup, {
      playerThresholds,
      clipKind,
      exerciseLabels,
    });
    detections.bh_left = fam.bh_left;
    detections.bh_topspin_left = fam.bh_topspin_left;
  }
  for (const id of ids) {
    if (id === "serve_toss") {
      detections[id] = detectServes(samples, viewSetup, {
        tableCorners: tableCorners || null,
      });
    }
  }
  if (!detections.serve_toss) {
    detections.serve_toss = detectServes(samples, viewSetup, {
      tableCorners: tableCorners || null,
    });
  }
  if (!Object.keys(detections).length) {
    return { forehand_count: 0, backhand_count: 0, serve_count: 0 };
  }
  return buildPoseSummaryFromDetections(detections);
}

export {
  detectServes,
  applyPlayerSignature,
  fitPlayerStrokeSignature,
  taggedSubset,
  strokesFromPhaseCycles,
};
